import { useState, useRef, useEffect } from "react";
import imageCompression from "browser-image-compression";
import { createBarber, updateBarber } from "../../api/barberApi";
import { useAuth } from "../../context/AuthContext";

export const useAddEditBarber = ({ onCreated, barber, onClearbarber }) => {
  const [firstName, setFirstName] = useState("");
  const [title, setTitle] = useState("Add");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState(false);
  useEffect(() => {
    if (!barber) return; // only run when message appears
    setFirstName(barber.firstName);
    setTitle("Edit");
    setLastName(barber.lastName);
    setPhoneNumber(barber.phoneNumber);
    setEmail(barber.email);
    setImagePreview(`${process.env.REACT_APP_Image_APP_URL}${barber.photoUrl}`);
    setImageFile();
  }, [barber]);
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  const validate = () => {
    if (!firstName.trim()) return "First name is required.";
    if (!lastName.trim()) return "Last name is required.";
    if (!phoneNumber.trim()) return "Phone number is required.";
    if (!email.trim()) return "Email is required.";
    if (imageFile) {
      const allowed = ["image/jpeg", "image/png", "image/webp"];
      if (!allowed.includes(imageFile.type))
        return "Only JPG, PNG or WEBP images are allowed.";
      if (imageFile.size > 2 * 1024 * 1024)
        return "Image must be smaller than 2 MB.";
    }
    return null;
  };

  const onFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const options = { maxWidthOrHeight: 550, maxSizeMB: 1, useWebWorker: true };
    const compressedFile = await imageCompression(file, options);

    setImageFile(compressedFile || null);

    if (imagePreview) URL.revokeObjectURL(imagePreview);
    const url = URL.createObjectURL(file);
    setImagePreview(url);
  };

  const clearForm = () => {
    setFirstName("");
    setLastName("");
    setPhoneNumber("");
    setEmail("");
    setImageFile(null);
    setTitle("Add");
    onClearbarber();
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowMessage(false);
    setMessage(null);

    const v = validate();
    if (v) {
      setMessage({
        text: v,
        type: "danger",
      });
      setShowMessage(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("FirstName", firstName.trim());
      formData.append("LastName", lastName.trim());
      formData.append("PhoneNumber", phoneNumber.trim());
      formData.append("Email", email.trim());
      formData.append("ShopId", user.barberShop);
      if (imageFile) formData.append("Image", imageFile, imageFile.name);
      if (barber) {
        formData.append("Id", barber.id);
        var res = await updateBarber(formData, user.barberShop);
      } else {
        var res = await createBarber(formData, user.barberShop);
      }
      onCreated();
      if (res.data.isSuccess) {
        setMessage({ text: res.data.value, type: "success" });
        setShowMessage(true);
        clearForm();
      } else {
        setMessage({
          text: res.data.errors.join("\n"),
          type: "danger",
        });
        setShowMessage(true);
      }
    } catch (err) {
      setMessage({ text: err.response?.data?.message || "Upload failed." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    firstName,
    title,
    setFirstName,
    lastName,
    setLastName,
    phoneNumber,
    setPhoneNumber,
    email,
    setEmail,
    imageFile,
    imagePreview,
    isSubmitting,
    showMessage,
    setShowMessage,
    message,
    fileInputRef,
    onFileChange,
    handleSubmit,
    clearForm,
  };
};
